// 클라우드 우체통 암호 — PC 쪽 server/apex/cloudcrypto.py 와 똑같은 형식이어야 서로 푼다.
//   덩어리: JSON → gzip → AES-256-GCM(IV 12바이트) → base64( 0x01 | IV | 암호문+태그 )
//   열쇠 상자: 기록 열쇠(32바이트)를 복구 코드로 잠근 것. 복구 코드 → PBKDF2-SHA256(소금 16바이트, 21만 회) → 잠금 열쇠
// 기록 열쇠는 이 기기 안(IndexedDB)에만 꺼내 둔다 (밖으로 꺼낼 수 없는 CryptoKey). 클라우드에는 잠긴 것만 간다.

const VERSION = 1;
export const KDF_ITER = 210_000;
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const enc = new TextEncoder();
const dec = new TextDecoder();

export interface Keybox { v: 1; kdf: 'PBKDF2-SHA256'; iter: number; salt: string; iv: string; wrapped: string }

const random = (n: number) => crypto.getRandomValues(new Uint8Array(n));

export function toB64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export function fromB64(text: string): Uint8Array {
  const bin = atob(text);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** 24자(120비트), 4자씩 끊어 표시. 헷갈리는 0·O·1·I 는 쓰지 않는다 */
export function newRecoveryCode(): string {
  const raw = Array.from(random(24), (b) => CODE_ALPHABET[b % 32]).join('');
  return (raw.match(/.{4}/g) as string[]).join('-');
}

export const normalizeCode = (code: string): string => code.toUpperCase().replace(/[^A-Z0-9]/g, '');

async function lockKey(code: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', enc.encode(normalizeCode(code)), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations }, base, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

const importDataKey = (raw: Uint8Array) => crypto.subtle.importKey('raw', raw as BufferSource, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);

/** 처음 설정: 새 기록 열쇠를 만들고 복구 코드로 잠근 상자를 함께 돌려준다 */
export async function createKey(code: string, iterations = KDF_ITER): Promise<{ key: CryptoKey; box: Keybox }> {
  const raw = random(32);
  const salt = random(16);
  const iv = random(12);
  const wrapped = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, await lockKey(code, salt, iterations), raw as BufferSource));
  const key = await importDataKey(raw);
  raw.fill(0);
  return { key, box: { v: 1, kdf: 'PBKDF2-SHA256', iter: iterations, salt: toB64(salt), iv: toB64(iv), wrapped: toB64(wrapped) } };
}

export async function openKeybox(box: Keybox, code: string): Promise<CryptoKey> {
  let raw: Uint8Array;
  try {
    raw = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromB64(box.iv) as BufferSource }, await lockKey(code, fromB64(box.salt), box.iter), fromB64(box.wrapped) as BufferSource));
  } catch {
    throw new Error('복구 코드가 맞지 않습니다');
  }
  const key = await importDataKey(raw);
  raw.fill(0);
  return key;
}

async function pipe(data: Uint8Array, stream: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  const out = new Blob([data as BlobPart]).stream().pipeThrough(stream);
  return new Uint8Array(await new Response(out).arrayBuffer());
}

export async function seal(key: CryptoKey, obj: unknown): Promise<string> {
  const raw = await pipe(enc.encode(JSON.stringify(obj)), new CompressionStream('gzip'));
  const iv = random(12);
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, raw as BufferSource));
  const out = new Uint8Array(13 + ct.length);
  out[0] = VERSION;
  out.set(iv, 1);
  out.set(ct, 13);
  return toB64(out);
}

export async function openSealed<T>(key: CryptoKey, blob: string): Promise<T> {
  const data = fromB64(blob);
  if (data[0] !== VERSION) throw new Error('모르는 형식의 기록 덩어리');
  let raw: Uint8Array;
  try {
    raw = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: data.subarray(1, 13) as BufferSource }, key, data.subarray(13) as BufferSource));
  } catch {
    throw new Error('기록 열쇠가 맞지 않습니다 (다른 계정의 기록이거나 열쇠가 바뀜)');
  }
  return JSON.parse(dec.decode(await pipe(raw, new DecompressionStream('gzip')))) as T;
}
